/**
 * Is a flex schedule inside its arming window right now? (ENG-1968)
 *
 * The Arm / restart-to-apply schedule picker used to list flex schedules with
 * no times, so a user applying settings ticked every schedule and armed the
 * night one at 10 am: a manual reboot sticks until the schedule's next
 * boundary, which for a 22:00–06:00 schedule is the next morning (SUP-669).
 *
 * Mirrors actuate_admin's ScheduleV2.is_running: today's enabled,
 * non-override row in the site's timezone; `[start, end)`; a window whose end
 * is not after its start wraps past midnight. Day values are "0" = Sunday …
 * "6" = Saturday, like JavaScript's getDay().
 */

export interface FlexScheduleRow {
  always_on?: boolean;
  day_of_week?: string[];
  enabled?: boolean;
  end_time?: string | null;
  is_override?: boolean;
  location_dusk_dawn?: unknown;
  start_time?: string | null;
}

export interface FlexWindow {
  /** true / false when it can be worked out; null for dusk-to-dawn or no data. */
  inWindow: boolean | null;
  /** "22:00–06:00", "Always on", "Dusk to dawn", or "Not scheduled today". */
  label: string;
  /** "HH:MM" the window ends, when it has fixed times. */
  endsAt: string | null;
}

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const hhmm = (time: string) => time.slice(0, 5);

/** Weekday (0 = Sunday) and minutes past midnight in `timeZone`. */
export const siteLocalNow = (
  timeZone: string | undefined,
  now: Date = new Date()
): { day: number; minutes: number } => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const get = (type: string) =>
      parts.find((part) => part.type === type)?.value ?? "";
    const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(
      get("weekday")
    );
    const minutes = Number(get("hour")) * 60 + Number(get("minute"));
    if (day >= 0 && !Number.isNaN(minutes)) return { day, minutes };
  } catch {
    // Unknown timezone string: fall through to the browser's clock.
  }
  return { day: now.getDay(), minutes: now.getHours() * 60 + now.getMinutes() };
};

const rowWindow = (row: FlexScheduleRow, minutes: number): FlexWindow => {
  if (row.always_on) {
    return { inWindow: true, label: "Always on", endsAt: null };
  }
  if (row.location_dusk_dawn) {
    return { inWindow: null, label: "Dusk to dawn", endsAt: null };
  }
  if (!row.start_time || !row.end_time) {
    return { inWindow: null, label: "No times set", endsAt: null };
  }
  const start = toMinutes(row.start_time);
  const end = toMinutes(row.end_time);
  const inWindow =
    end > start
      ? start <= minutes && minutes < end
      : minutes < end || start <= minutes;
  return {
    inWindow,
    label: `${hhmm(row.start_time)}–${hhmm(row.end_time)}`,
    endsAt: hhmm(row.end_time),
  };
};

export const flexScheduleWindow = (
  rows: FlexScheduleRow[] | undefined,
  timeZone: string | undefined,
  now: Date = new Date()
): FlexWindow => {
  const { day, minutes } = siteLocalNow(timeZone, now);
  // A schedule can hold more than one row for the same weekday (two windows
  // in a day), so evaluate them all: running if any row is.
  const today = (rows ?? [])
    .filter(
      (row) =>
        row.enabled !== false &&
        !row.is_override &&
        (row.day_of_week ?? []).includes(String(day))
    )
    .map((row) => rowWindow(row, minutes));

  if (today.length === 0) {
    return { inWindow: false, label: "Not scheduled today", endsAt: null };
  }
  const running = today.find((window) => window.inWindow === true);
  if (running) return running;
  const label = today.map((window) => window.label).join(", ");
  const unknown = today.find((window) => window.inWindow === null);
  if (unknown) return { inWindow: null, label, endsAt: null };
  return { inWindow: false, label, endsAt: today[0].endsAt };
};
